'use memo';

import React, { useContext, useState } from 'react';
import {
  Images,
  AgxSlideUpModal,
  DocumentTypes,
  DocumentTypesMap,
  AgxMultiOptionButton,
  FormConfigType,
  type MultiOptionButtonOption,
  type UploadedDocument as OldUploadedDocument,
} from '@urbanx/agx-ui-components';
import { AgxButton, AgxDivider, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import { useGetFileLink } from 'components/ui-components/File/fileApi';
import { AuthContext } from '@urbanx/agx.auth.client';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useFormLoader } from 'hooks/useFormLoader';
import { Tabs } from 'types/Tabs';
import { setAndShowErrorToast } from 'store/config';
import { removeFormSubmission } from '../../../../../../campaigns/campaignsReducer';
import useStateFormLabels from '../../../../../../form/FormLabels/useStateFormLabels';
import { openFileInNewTab } from 'utils/openFileInNewTab';
import './SentForSigningButtons.scss';
import { sortDocumentsByType } from 'utils/documentUtil';
import { isNil } from 'lodash-es';
import {
  CampaignDetailModel,
  CampaignDetailModelState,
  UploadedDocumentDocumentType,
} from '@urbanx/agx.campaigns.client';

interface SentForSigningButtonsProps {
  campaign: CampaignDetailModel;
  envelopeId: string;
  currentTab: Tabs;
  isMobile?: boolean;
  isDesktop?: boolean;
}

export const SentForSigningButtons: React.FC<SentForSigningButtonsProps> = ({
  campaign,
  envelopeId,
  currentTab,
  isMobile = false,
  isDesktop = false,
}) => {
  const { getAuthToken } = useContext(AuthContext);
  const dispatch = useAppDispatch();
  const { loadForm } = useFormLoader();
  const [showConfirmationModel, setShowConfirmationModel] = useState(false);
  const [disableInputs, setDisableInputs] = useState(false);
  const { completionState, campaignId, documents } = campaign;
  const formName = useStateFormLabels(campaign.state ?? CampaignDetailModelState.QLD, 'formName');
  const getFileLink = useGetFileLink();

  const documentTypes = documents?.map((doc) => doc.documentType);
  const uniqueDocumentTypes = new Set(documentTypes);
  const noAttachments =
    uniqueDocumentTypes.size === 2 &&
    uniqueDocumentTypes.has(UploadedDocumentDocumentType.AgencyAgreement) &&
    uniqueDocumentTypes.has(UploadedDocumentDocumentType.MergedAgencyAgreement);

  const fileDownloadOptions: MultiOptionButtonOption[] = [];

  if (documents && documents.length > 0) {
    const miscDocumentTypes = [DocumentTypes.Miscellaneous, DocumentTypes.AdditionalAnnexure];

    const orderedDocuments = sortDocumentsByType(documents as unknown as OldUploadedDocument[]);

    const nonMiscDocuments = orderedDocuments.filter(
      (d, ind, arr) =>
        !miscDocumentTypes.includes(d.documentType) &&
        (!ind || d.documentType !== arr[ind - 1]?.documentType)
    );
    const miscDocuments = orderedDocuments.filter((d) =>
      miscDocumentTypes.includes(d.documentType)
    );

    nonMiscDocuments.forEach((d) => {
      fileDownloadOptions.push({
        text: DocumentTypesMap[d.documentType as keyof typeof DocumentTypesMap],
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });

    miscDocuments.forEach((d) => {
      fileDownloadOptions.push({
        text: d.fileName,
        onClick: async () => {
          const fileLink = await getFileLink(d.containerFilePath);
          if (fileLink != null) {
            openFileInNewTab(isDesktop, fileLink);
          }
        },
      });
    });
  }

  const onEditAgencyAgreementClick = () => {
    setShowConfirmationModel(true);
  };

  const onConfirmEdit = async () => {
    if (!campaignId || !envelopeId) return;

    const inProgressForm = campaign.inProgressForms != null ? campaign.inProgressForms[0] : null;

    if (inProgressForm != null) {
      try {
        setDisableInputs(true);
        const authToken = await getAuthToken();

        if (!authToken) {
          setDisableInputs(false);
          return;
        }

        await removeFormSubmission(authToken, campaignId, envelopeId);

        await loadForm(campaignId, inProgressForm.formId ?? '', FormConfigType.Campaign);
      } catch (err) {
        console.error(err);
        dispatch(setAndShowErrorToast('Failed to void envelope'));
      }

      setDisableInputs(false);
    }
  };

  const ViewDocumentsButton = () => {
    if (fileDownloadOptions.length === 0) return <></>;

    return fileDownloadOptions.length === 1 || noAttachments ? (
      <AgxButton
        id="downloadContract"
        text="Download"
        variant="hollow"
        disabled={currentTab === Tabs.Archive}
        size={'large'}
        onClick={fileDownloadOptions[0].onClick}
      />
    ) : (
      <AgxMultiOptionButton
        id="downloadContracts"
        text="Download"
        hollow
        disabled={currentTab === Tabs.Archive}
        large
        options={fileDownloadOptions}
      />
    );
  };

  const confirmationDialogBody = (
    <AgxSpacer direction="vertical" gap="spacing-xl" className="confirmationModalStyle">
      <Images.AlertCircleOutline />
      <AgxTypography id="void-envelope-confirmation-title" type="heading" size="large">
        Editing {formName} will void current DocuSign envelope
      </AgxTypography>
      <AgxTypography id="void-envelope-confirmation-body" type="body" size="medium">
        Current recipients will be notified, you'll be able to generate a new {formName} and send a
        new DocuSign envelope.
      </AgxTypography>
      <AgxTypography id="void-envelope-confirmation-question" type="body" size="medium">
        Continue to edit?
      </AgxTypography>
      {isMobile && <AgxDivider variant="horizontal" />}
      <div className={'confirmationButtonContainerStyle'}>
        <AgxButton
          id="voidnedit"
          size="large"
          variant="primary"
          text="Void and Edit"
          disabled={disableInputs}
          onClick={onConfirmEdit}
        />
        <AgxButton
          id="cancel"
          variant="hollow"
          size="large"
          text="No, go back"
          disabled={disableInputs}
          onClick={() => setShowConfirmationModel(false)}
        />
      </div>
    </AgxSpacer>
  );

  return (
    <>
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="space-between"
        gap="sm"
      >
        <ViewDocumentsButton />
        <AgxButton
          id="editSubmission"
          key="editSubmission"
          variant="hollow"
          size={'large'}
          text="Edit"
          onClick={onEditAgencyAgreementClick}
          disabled={
            disableInputs || !isNil(completionState?.completionDate) || currentTab === Tabs.Archive
          }
          stretch
        />
      </AgxSpacer>

      {showConfirmationModel && (
        <AgxSlideUpModal
          closeButton
          content={confirmationDialogBody}
          desktop={!isMobile}
          onClose={() => setShowConfirmationModel(false)}
        />
      )}
    </>
  );
};
